import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import universityAPI, { UNIVERSITY_ENDPOINT } from 'src/api/university';
import { SWRConfig } from 'swr';

import Container from './style';

import { LoadingPage } from '@components/atoms/loading';
import Seo from '@components/atoms/seo';
import { AboutSection } from '@components/organisms/degreeLandingPage/aboutSection';
import { CoursesSection } from '@components/organisms/degreeLandingPage/coursesSection';
import { DegreeHighlightSection } from '@components/organisms/degreeLandingPage/degreeHighlight';
import { DegreeStructSection } from '@components/organisms/degreeLandingPage/degreeStructSection';
import Header from '@components/organisms/degreeLandingPage/header';
import { TotalExpenseSection } from '@components/organisms/degreeLandingPage/totalExpenseSection';
import VideoPreviewSection from '@components/organisms/degreeLandingPage/videoPreviewSection';
import Main from '@components/templates/main';
import { withForwardedIp } from '@helpers/forwardedIp';
import { getNamespaces } from '@helpers/i18nUtils';
import { useInsiderV2 } from '@hooks/dataTracking/useInsiderV2';
import { DegreeSectionType, IDegreeLandingPageData, StatusDegreeUniversity } from '@interfaces/university';

type DegreeLandingPageProps = {
  fallback: Record<string, { data: unknown }>;
};

const DegreeLandingPage = ({ fallback }: DegreeLandingPageProps) => {
  const router = useRouter();
  const degreeIdName = (router.query.degreeIdName as string) || '';
  const { t } = useTranslation(getNamespaces({ namespaces: [degreeIdName] }));

  const universityId = router.query.universityIdName as string;
  const degreeId = router.query.degreeIdName as string;

  const landingPageData = fallback[UNIVERSITY_ENDPOINT.DEGREE_LANDING_PAGE(universityId, degreeId)]
    .data as IDegreeLandingPageData;

  const isDraft = !landingPageData || landingPageData.degreeStatus === StatusDegreeUniversity.Draft;
  const isComingSoon = landingPageData && landingPageData.degreeStatus === StatusDegreeUniversity.ComingSoon;

  const { insider_dispatch, sendInsiderListingPageView } = useInsiderV2();
  const insiderRef = useRef<boolean>(false);

  useEffect(() => {
    if (!landingPageData || insiderRef.current) {
      return;
    }

    insiderRef.current = true;

    insider_dispatch([
      () =>
        sendInsiderListingPageView({
          taxonomy: [landingPageData.degreeName, landingPageData.majorName],
        }),
    ]);

    setTimeout(() => {
      insiderRef.current = false;
    }, 1000);
  }, [landingPageData]);

  useEffect(() => {
    if (isDraft) {
      router.push('/');
      return;
    }

    if (isComingSoon) {
      router.push(`/academic/${universityId}/degree/${degreeId}/coming-soon`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraft, isComingSoon]);

  if (isDraft || isComingSoon) return <LoadingPage />;

  const degreeVideosPreview = landingPageData?.highlightVideos;
  const degreeVideosPreviewHighlight = degreeVideosPreview?.filter(
    (degreeVideoPreview) => degreeVideoPreview.type === DegreeSectionType.HIGHLIGHT,
  );
  const haveDegreeVideoPreviewHighlight = degreeVideosPreviewHighlight && degreeVideosPreviewHighlight.length > 0;

  const { degreeName, majorName } = landingPageData;

  return (
    <SWRConfig value={{ fallback }}>
      <Main>
        <Seo
          title={t('degree_landing_page.header.title', { degreeName: landingPageData.degreeName })}
          description={landingPageData.aboutDegree}
          image_url={landingPageData?.degreeBannerUrl}
        />
        <Header />
        <Container>
          {haveDegreeVideoPreviewHighlight && degreeName && majorName && (
            <VideoPreviewSection videos={degreeVideosPreviewHighlight} degreeName={degreeName} majorName={majorName} />
          )}
          <AboutSection />
          <CoursesSection />
          <DegreeHighlightSection />
        </Container>
        <DegreeStructSection />
        <Container>
          <TotalExpenseSection />
        </Container>
      </Main>
    </SWRConfig>
  );
};

export const getServerSideProps: GetServerSideProps = withForwardedIp(async (ctx) => {
  const locale = ctx.locale as string;
  const universityId = (ctx.params?.universityIdName as string) || '';
  const degreeId = (ctx.params?.degreeIdName as string) || '';

  const namespaces = getNamespaces({
    namespaces: [degreeId],
  });

  const response = await universityAPI.getDegreeLandingPageData(universityId, degreeId);

  return {
    props: {
      fallback: {
        [UNIVERSITY_ENDPOINT.DEGREE_LANDING_PAGE(universityId, degreeId)]: response.data || [],
      },
      ...(await serverSideTranslations(locale, namespaces)),
    },
  };
});

export default DegreeLandingPage;
